// Page Path : src\pages\design\document\configuration\toolbar.tsx

import { Box, Input, Typography } from '@mui/material';

import RotateRight from 'assets/images/configuration/rotate-right.svg';
import RotateLeft from 'assets/images/configuration/rotate-left.svg';
import ZoomIn from 'assets/images/configuration/zoom-in.svg';
import ZoomOut from 'assets/images/configuration/zoom-out.svg';
import Resize from 'assets/images/configuration/resize.svg';
import FitScreen from 'assets/images/configuration/fit-screen.svg';
import NewTab from 'assets/images/configuration/open-in-new.svg';
import ChevronLeft from 'assets/images/configuration/chevron-left.svg';
import ChevronRight from 'assets/images/configuration/chevron-right.svg';
import { Tooltip } from 'components/elements';
import { useCallback, useEffect, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import IconButton from 'components/@extended/IconButton';
import DynamicSearchWithArrow from 'components/forms/DynamicSearchWithArrow';
import { GetFileHeaderDataResponse, SearchTextInFileResponse } from 'types/image-viewer';
import useScreenSize from 'hooks/useScreenSize';

interface Props {
  fileHeaderData: GetFileHeaderDataResponse['headerData'];
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRubberbandSelect: () => void;
  onRubberbandUnSelect: () => void;
  onFitToScreenSelect: () => void;
  onFitToScreenUnSelect: () => void;
  onDualScreen: () => void;
  isSecondaryScreen?: boolean;
  currentZoomLevel?: number;
  isMaxZoomIn?: boolean;
  isMaxZoomOut?: boolean;
  currentPage: number;
  totalPages: number;
  fitToScreen?: boolean;
  onPageNumberChange: (pageNumber: number) => void;
  searchTextInFile: (_value: string) => Promise<any[]>;
  onSelectSearchTextInFile?: (_a: SearchTextInFileResponse) => void;
  disableRubberband: boolean;
  setActiveToolbarElement?: any;
  fileNavigation?: boolean;
  setFileNavigation?: any;
  imageLoading: boolean;
  enableRubberBand: boolean;
}

const ToolDivider = () => <Box className="tool-divider" />;
const ToolDividers = () => <Box className="tool-divider  customise" />;

enum ToolsEnum {
  RotateLeft = 'rotate-left',
  RotateRight = 'rotate-right',
  ZoomIn = 'zoom-in',
  ZoomOut = 'zoom-out',
  Rubberband = 'rubber-band',
  FitToScreen = 'fit-to-screen',
  DualScreen = 'dual-screen'
}

const POSITION_OFFSET = {
  TOP: { x: '50%', y: 0 },
  RIGHT: { x: '100%', y: '50%' },
  BOTTOM: { x: '50%', y: '100%' },
  LEFT: { x: '0', y: '50%' }
};

const CanvasToolbar = ({
  fileHeaderData,
  onRotateLeft,
  onRotateRight,
  onZoomIn,
  onZoomOut,
  onRubberbandSelect,
  onRubberbandUnSelect,
  onFitToScreenSelect,
  onFitToScreenUnSelect,
  onDualScreen,
  isSecondaryScreen,
  currentZoomLevel,
  isMaxZoomIn,
  isMaxZoomOut,
  currentPage,
  totalPages,
  onPageNumberChange,
  searchTextInFile,
  onSelectSearchTextInFile,
  fitToScreen = false,
  disableRubberband,
  setActiveToolbarElement,
  fileNavigation,
  setFileNavigation,
  imageLoading,
  enableRubberBand
}: Props) => {
  const [active, setActive] = useState();
  const [currentPageInput, setCurrentPageInput] = useState<string>(`${currentPage}`);
  const [positionOffset, setPositionOffset] = useState<{ x: number | string; y: number | string }>({ x: '50%', y: 0 });
  const { isMobile } = useScreenSize();
  const pageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentPageInput(`${currentPage}`);
  }, [currentPage]);

  useEffect(() => {
    if (!imageLoading && fileHeaderData && Object.keys(fileHeaderData).length > 0) {
      setTimeout(() => {
        onRubberbandSelect();
      }, 5);
    }
  }, [imageLoading]);

  useEffect(() => {
    const initOffset = !isMobile ? POSITION_OFFSET.TOP : POSITION_OFFSET.RIGHT;
    setPositionOffset(initOffset);
  }, [isMobile]);

  useEffect(() => {
    if (setActiveToolbarElement) setActiveToolbarElement(active);
  }, [active, setActiveToolbarElement]);

  const onPageNumberInputSubmit = useCallback(() => {
    if (currentPageInput && currentPage !== Number(currentPageInput)) {
      onPageNumberChange(Number(currentPageInput));
    }
  }, [currentPage, currentPageInput, onPageNumberChange]);

  const checkForEnter = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        e.target.blur();
        onPageNumberInputSubmit();
      }
    },
    [onPageNumberInputSubmit]
  );

  const onToolSelect = useCallback(
    (_next: ToolsEnum) => {
      switch (_next) {
        case ToolsEnum.RotateLeft:
          onRotateLeft();
          break;
        case ToolsEnum.RotateRight:
          onRotateRight();
          break;
        case ToolsEnum.ZoomIn:
          onZoomIn();
          break;
        case ToolsEnum.ZoomOut:
          onZoomOut();
          break;
        case ToolsEnum.Rubberband:
          enableRubberBand ? onRubberbandUnSelect() : onRubberbandSelect();
          break;
        case ToolsEnum.FitToScreen:
          fitToScreen ? onFitToScreenUnSelect() : onFitToScreenSelect();
          break;
        case ToolsEnum.DualScreen:
          onDualScreen();
          break;
      }
    },
    [
      onDualScreen,
      onFitToScreenSelect,
      onRotateLeft,
      onRotateRight,
      onRubberbandSelect,
      onRubberbandUnSelect,
      onZoomIn,
      onZoomOut,
      onFitToScreenUnSelect,
      fitToScreen,
      enableRubberBand
    ]
  );

  const onToolUnselect = useCallback(
    (prev: ToolsEnum) => {
      switch (prev) {
        case ToolsEnum.Rubberband:
          onRubberbandUnSelect();

          break;
        case ToolsEnum.FitToScreen:
          onFitToScreenUnSelect();
          break;
      }
    },
    [onFitToScreenUnSelect, onRubberbandUnSelect]
  );

  const onToolReset = useCallback(
    (prev: ToolsEnum) => {
      switch (prev) {
        case ToolsEnum.Rubberband:
          onRubberbandUnSelect();
          break;
      }
    },
    [onRubberbandUnSelect]
  );

  const onClick = useCallback(
    (e: any) => {
      const _id = e.target.id;
      setActive((prev) => {
        const isToggle = prev && e.target.hasAttribute('data-is-toggle');
        if (isToggle) {
          if (prev === _id && ![ToolsEnum.FitToScreen, ToolsEnum.Rubberband].includes(prev)) {
            onToolUnselect(prev);
          } else {
            onToolReset(prev);
          }
        }
        const _next = isToggle && prev === _id && ![ToolsEnum.FitToScreen, ToolsEnum.Rubberband].includes(prev) ? undefined : _id;

        if (_next) {
          onToolSelect(_next);
        }
        return _next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onToolSelect, onToolUnselect]
  );

  const onPrevPageClick = useCallback(() => {
    const temppage = currentPageInput !== '' ? currentPageInput : currentPage;
    const temppagenum = Math.max(1, Number(temppage) - 1);
    onPageNumberChange(temppagenum);
  }, [currentPageInput, onPageNumberChange]);

  const onNextPageClick = useCallback(() => {
    const temppage = currentPageInput !== '' ? currentPageInput : currentPage;
    const temppagenum = Math.min(totalPages, Number(temppage) + 1);
    onPageNumberChange(temppagenum);
  }, [currentPageInput, onPageNumberChange, totalPages]);

  const onDragStop = useCallback((e: DraggableEvent, data: DraggableData) => {
    const toolbar = data.node.children[0];
    const toolbarRect = toolbar.getBoundingClientRect();
    const parent = data.node.parentElement?.getBoundingClientRect();

    if (!parent) {
      return;
    }
    setPositionOffset((prev) => {
      let offset = { ...prev };
      const topDiff = toolbarRect.top - parent?.top;
      const rightDiff = parent?.right - toolbarRect.right;
      const leftDiff = toolbarRect.left - parent?.left;
      const bottomDiff = parent?.bottom - toolbarRect.bottom;

      const minDiff = Math.min(topDiff, rightDiff, leftDiff, bottomDiff);

      switch (minDiff) {
        case topDiff:
          offset = POSITION_OFFSET.TOP;
          break;
        case rightDiff:
          offset = POSITION_OFFSET.RIGHT;
          break;
        case bottomDiff:
          offset = POSITION_OFFSET.BOTTOM;
          break;
        case leftDiff:
          offset = POSITION_OFFSET.LEFT;
          break;
      }
      return offset;
    });
  }, []);

  return (
    <Draggable onStop={onDragStop} positionOffset={positionOffset} position={{ x: 0, y: 0 }} cancel=".default-icon">
      <Box className="toolbar-wrapper">
        <Box
          className={`canvas-toolbar ${positionOffset.x !== '50%' ? 'vertical' : ''} ${positionOffset.x === '100%' ? 'right-edge' : ''} ${
            positionOffset.y === '100%' ? 'bottom-edge' : ''
          }`}
          onClick={onClick}
        >
          <Tooltip title="Rotate left" placement="top" arrow={false}>
            <Box
              component="img"
              src={RotateLeft}
              id={ToolsEnum.RotateLeft}
              className={active === ToolsEnum.RotateLeft ? 'active' : 'default-icon'}
            />
          </Tooltip>
          <Tooltip title="Rotate right" placement="top" arrow={false}>
            <Box
              component="img"
              src={RotateRight}
              id={ToolsEnum.RotateRight}
              className={active === ToolsEnum.RotateRight ? 'active' : 'default-icon'}
            />
          </Tooltip>
          <ToolDivider />
          <Tooltip
            title={`${isMaxZoomOut ? 'Max zoom out reached' : 'Zoom out'} (Current: ${currentZoomLevel}%)`}
            placement="top"
            arrow={false}
          >
            <Box
              component="img"
              src={ZoomOut}
              id={ToolsEnum.ZoomOut}
              className={active === ToolsEnum.ZoomOut ? 'active' : 'default-icon'}
            />
          </Tooltip>
          <Tooltip
            title={`${isMaxZoomIn ? 'Max zoom in reached' : 'Zoom in'} (Current: ${currentZoomLevel}%) `}
            placement="top"
            arrow={false}
          >
            <Box component="img" src={ZoomIn} id={ToolsEnum.ZoomIn} className={active === ToolsEnum.ZoomIn ? 'active' : 'default-icon'} />
          </Tooltip>

          <ToolDivider />
          <Tooltip title="Rubber band" placement="top" arrow={false}>
            <Box
              component="img"
              src={Resize}
              data-is-toggle
              id={ToolsEnum.Rubberband}
              className={enableRubberBand && !disableRubberband ? 'active' : 'default-icon'}
              sx={{ pointerEvents: disableRubberband ? 'none' : 'all' }}
            />
          </Tooltip>
          <ToolDivider />
          <Tooltip title="Fit to screen" placement="top" arrow={false}>
            <Box
              component="img"
              src={FitScreen}
              data-is-toggle
              id={ToolsEnum.FitToScreen}
              className={fitToScreen ? 'active' : 'default-icon'}
            />
          </Tooltip>
          {!isSecondaryScreen && (
            <Tooltip title="Dual screen" placement="top" arrow={false}>
              <Box
                component="img"
                src={NewTab}
                id={ToolsEnum.DualScreen}
                className={active === ToolsEnum.DualScreen ? 'active' : 'default-icon'}
              />
            </Tooltip>
          )}
          <ToolDivider />
          <Typography className="page-label">Page</Typography>
          <IconButton
            disabled={currentPage <= 1 || imageLoading}
            onClick={onPrevPageClick}
            className="page-navigation-btn page-navigation-backward"
          >
            <Box component="img" src={ChevronLeft} />
          </IconButton>
          <div className="page-details">
            <div className="page-details">
              <Input
                type="number"
                value={currentPageInput}
                ref={pageInputRef}
                onBlur={onPageNumberInputSubmit}
                onKeyUp={checkForEnter}
                disabled={imageLoading}
                onKeyDown={(e) => {
                  // Prevent typing +, -, e, or any other non-numeric characters
                  if (['-', '+', 'e'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow user to clear the input completely
                  if (value === '') {
                    setCurrentPageInput('');
                    return;
                  }

                  const numericValue = Number(value);

                  // Update the state with valid values, constrained to totalPages
                  if (numericValue >= 1 && numericValue <= totalPages) {
                    setCurrentPageInput(numericValue.toString());
                  } else if (numericValue < 1) {
                    setCurrentPageInput('1'); // Set to 1 if the value is less than 1
                  } else {
                    setCurrentPageInput(totalPages.toString()); // Set to totalPages if it exceeds totalPages
                  }
                }}
                className="page-number-input"
                inputProps={{
                  min: 1,
                  max: totalPages,
                  pattern: '[0-9]*' // Pattern to allow only numbers
                }}
              />
            </div>

            {positionOffset.x !== '50%' ? <ToolDividers /> : <Typography className="page-input-separator"></Typography>}

            <Typography>{totalPages}</Typography>
          </div>
          <IconButton
            disabled={currentPage >= totalPages || imageLoading}
            onClick={onNextPageClick}
            className="page-navigation-btn page-navigation-forward"
          >
            <Box component="img" src={ChevronRight} />
          </IconButton>
          <Box>
            <DynamicSearchWithArrow
              fetchData={searchTextInFile}
              onSelectData={onSelectSearchTextInFile as any}
              fileNavigation={fileNavigation}
              setFileNavigation={setFileNavigation}
            />
          </Box>
        </Box>
      </Box>
    </Draggable>
  );
};

export default CanvasToolbar;
